import { orderTypeConstants } from "constants/order";
import { distributionLogicValueList } from "constants/paymentPlanConstants";
import { knownPracticeTypeCodeOrNull } from "constants/practiceType";
import dayjs from "dayjs";
import { parseDate, parseTime } from "helpers/date";
import { removeClearPatientFee } from "helpers/fee";
import {
  resolveOrderBenefits,
  resolveOrderSecondaryBenefits,
} from "helpers/orderBenefits";
import { centsToDollars } from "helpers/price";
import { usePayerInformation } from "hooks/usePayerInformation";
import { CLEAR_HEALTH_PAYER_NAME } from "hooks/useProviderProcedure";
import { useMemo, useEffect, useState } from "react";
import { useForm, FormProvider } from "react-hook-form";
import { useDispatch } from "react-redux";
import { useSearchProviderProcedureMutation } from "store/queries/providerProcedure";
import {
  setAutoCompleteValue,
  setRows,
  setSelectedProcedures,
} from "store/slices/serviceDetails";
import {
  IOrder,
  IGFEServiceDetails,
  IDefaultServiceDetails,
  IPatientResponsibilityServiceDetails,
} from "types/Order/Order";

import {
  VisitForm,
  StickyFooter,
  EstimateResults,
  Submit,
  ServiceDetails,
  Communication,
  PatientBenefitsSection,
} from "./components";
import { OrderFormHeader } from "./components/OrderFormHeader";
import { readNoDeductibleChoice } from "./components/PatientBenefitsSection/noDeductibleChoice";
import { PatientStep } from "./components/PatientStep";
import { PaymentPlan } from "./components/PaymentPlan/PaymentPlan";
import {
  buildSelectedProceduresArray,
  buildSelectedProceduresObject,
  buildSelectedProceduresToTable,
} from "./components/ServiceDetails/builders";
import {
  formatDateOfServiceForProviderProcedureSearch,
  getPaymentPlanMinDownPaymentFlatValue,
} from "./utils/helpers";
import { formatPhoneNumberToMask } from "./utils/mask";

export function RedoOrder({
  order,
  handleSwitchRedoOrder,
}: {
  order: IOrder;
  handleSwitchRedoOrder: () => void;
}) {
  const dispatch = useDispatch();
  const [searchMutation] = useSearchProviderProcedureMutation();
  const [currentProviderProcedures, setCurrentProviderProcedures] =
    useState<any>([]);

  const {
    serviceDetails,
    patient,
    procedures,
    providerProcedures,
    amountInCents,
    accountNumber,
    dateOfService,
    orderType,
    accountId,
    gfeQuote,
    gfeQuoteId,
    id,
    paymentPlan,
    account,
    outreachStopped,
  } = order;

  const { payerNameOptions } = usePayerInformation(accountId);

  const estimatedVisitCost = useMemo(() => {
    if (orderType === orderTypeConstants.GFE) {
      return centsToDollars(
        (serviceDetails as IGFEServiceDetails)
          .patientResponsibilityEstimateAmountInCents || 0
      );
    }
    return centsToDollars(
      (serviceDetails as IDefaultServiceDetails).estimatedVisitCost || 0
    );
  }, [orderType, serviceDetails]);

  const payerName = useMemo(() => {
    if (orderType === orderTypeConstants.GFE) {
      return payerNameOptions.find(
        (aOption: { name: string; value: string }) =>
          aOption.name.toLowerCase() === order?.payer?.name?.toLowerCase()
      );
    }
    return null;
  }, [orderType, payerNameOptions, order?.payer?.name]);

  const payerName2 = useMemo(() => {
    if (orderType === orderTypeConstants.GFE) {
      return payerNameOptions.find(
        (aOption: { name: string; value: string }) =>
          aOption?.name?.toLowerCase() ===
          order?.secondaryPayer?.name?.toLowerCase()
      );
    }
    return null;
  }, [orderType, payerNameOptions, order?.secondaryPayer?.name]);

  const paymentPlanActive = useMemo(
    () => paymentPlan.find((paymentPlan) => paymentPlan.active),
    [paymentPlan]
  );

  const selectedProceduresList = useMemo(() => {
    return serviceDetails.procedures.map(
      (procedure) => `${procedure.code} - ${procedure.cptDescription}`
    );
  }, [serviceDetails]);

  const sendDefaultText = useMemo(() => {
    if (
      order.scheduledFirstCommunicationDate &&
      !order.scheduledFirstCommunicationSent &&
      order.scheduledCommunicationCustomText
    ) {
      return false;
    }
    return true;
  }, [order]);

  // Calculate downpayment only flat amount with clear patient fee adjustment
  const paymentPlanMinDownPaymentFlatValue = useMemo(
    () =>
      getPaymentPlanMinDownPaymentFlatValue({
        paymentPlanMinDownPaymentFlat:
          paymentPlanActive?.paymentPlanMinDownPaymentFlat,
        orderType,
        clearPatientFeeValueInPercentage:
          order.clearPatientFeeValueInPercentage,
      }),
    [
      paymentPlanActive?.paymentPlanMinDownPaymentFlat,
      orderType,
      order.clearPatientFeeValueInPercentage,
    ]
  );

  const initializeDefault = async () => {
    const autoCompleteValue = [
      ...new Set(
        serviceDetails.procedures.map(
          (procedure) =>
            `${procedure.code} - ${
              procedures?.find((p) => p.cptCode === procedure.code.toString())
                ?.description
            }`
        )
      ),
    ];
    const selectedProcedureArrayWithDefaultDescriptions =
      buildSelectedProceduresArray({
        procedures: (serviceDetails as IGFEServiceDetails).procedures.map(
          (procedure) => {
            const defaultDescription = procedures?.find(
              (p) => p.cptCode === procedure.code.toString()
            )?.description;
            return {
              code: procedure.code,
              providerProcedureId: procedure.providerProcedureId,
              specialty: procedure.specialty,
              cptDescription: defaultDescription || "",
            };
          }
        ),
      });
    const selectedProcedureArray = buildSelectedProceduresArray({
      procedures: (serviceDetails as IGFEServiceDetails).procedures.map(
        (procedure) => {
          return {
            code: procedure.code,
            providerProcedureId: procedure.providerProcedureId,
            specialty: procedure.specialty,
            cptDescription: procedure.cptDescription,
          };
        }
      ),
    });
    const selectedProceduresObject = buildSelectedProceduresObject(
      selectedProcedureArray
    );
    const rowsToSet = buildSelectedProceduresToTable({
      selectedProceduresObject,
      procedures: procedures || [],
      providerProcedures: providerProcedures || [],
      rows: [],
    });
    const rowsWithoutDuplicateProviders = rowsToSet.map((row: any) => {
      const uniqueProviders = row.providerDropdown.filter(
        (provider: any, index: any, self: any) =>
          index === self.findIndex((p: any) => p.id === provider.id)
      );
      return {
        ...row,
        providerDropdown: uniqueProviders,
      };
    });

    // Build the table rows from the saved order. `fetchedProviderProcedures` only ever
    // ADDS alternatives to each row's `providerDropdown` — selectedProvider, amount and
    // providerProcedureId always come from the saved order — so this is safe to run with
    // an empty list first (to paint the table immediately) and again once the fetch lands.
    const buildRows = (fetchedProviderProcedures: any[]) => {
      const rowsWithExpandedDropdowns = rowsWithoutDuplicateProviders.map(
        (row: any) => {
          const providerData = [...row.providerDropdown];
          fetchedProviderProcedures.forEach((providerProcedure: any) => {
            if (
              row.cptCode === providerProcedure?.procedure?.cptCode &&
              row.specialty === providerProcedure?.provider?.specialty &&
              row.procedureId === providerProcedure?.procedureId &&
              !providerData.find(
                (p: any) => p.id === providerProcedure?.provider?.id
              )
            ) {
              providerData.push({
                id: providerProcedure.provider.id,
                name: providerProcedure.provider.name,
                specialty: providerProcedure.provider.specialty,
                priceInCents: providerProcedure.priceInCents,
              });
            }
          });
          return { ...row, providerDropdown: providerData };
        }
      );

      // Match each row against the saved order's procedure so we display saved
      // provider/amount/providerProcedureId verbatim.
      const rowsWithAllAvailableProviders = rowsWithExpandedDropdowns.map(
        (row) => {
          const originalProcedure = (
            serviceDetails as IGFEServiceDetails
          ).procedures.find(
            (p) => p.code === row.cptCode && p.specialty === row.specialty
          );

          let { selectedProvider } = row;
          let selectedAmount = row.amount;
          let selectedProviderProcedureId = row.providerProcedureId;

          if (originalProcedure?.providerId) {
            const matchingProvider = row.providerDropdown.find(
              (p: any) => p.id === originalProcedure.providerId?.toString()
            );

            if (matchingProvider) {
              selectedProvider = matchingProvider;
              selectedAmount =
                originalProcedure.amountInCents !== undefined &&
                originalProcedure.amountInCents !== null
                  ? originalProcedure.amountInCents
                  : matchingProvider.priceInCents;

              if (originalProcedure.providerProcedureId) {
                selectedProviderProcedureId =
                  originalProcedure.providerProcedureId;
              }
            }
          } else if (
            originalProcedure?.amountInCents !== undefined &&
            originalProcedure?.amountInCents !== null
          ) {
            selectedAmount = originalProcedure.amountInCents;
          }

          return {
            ...row,
            selectedProvider,
            amount: selectedAmount,
            providerProcedureId: selectedProviderProcedureId,
          };
        }
      );

      return rowsWithAllAvailableProviders.map((row) => {
        const procedure = serviceDetails.procedures.find(
          (p) =>
            p.code === row.cptCode &&
            p.specialty === row.specialty &&
            p.providerId === row.selectedProvider.id &&
            p.providerProcedureId === row.providerProcedureId
        );
        return {
          ...row,
          benefitSelectionType:
            procedure?.benefitsForProvProc?.benefitSelectionType,
        };
      });
    };

    // Ensure each saved providerProcedure carries a nested `procedure`
    // (with cptCode), since downstream code — e.g. AutoCompleteCPT's unselect
    // filter on `pp.procedure.cptCode` — expects the same shape returned by
    // the live searchMutation.
    const normalizedProviderProcedures = (providerProcedures || []).map(
      (pp) => {
        if (pp?.procedure?.cptCode) return pp;
        const matchedProcedure = procedures?.find(
          (p) => Number(p.id) === Number(pp?.procedureId)
        );
        return matchedProcedure ? { ...pp, procedure: matchedProcedure } : pp;
      }
    );

    // Paint the table from the saved order BEFORE going to the network. Everything above
    // is derived from data we already hold, and quantity is row multiplicity — so if we
    // wait for the fetch (as we used to) the quantity column renders unhydrated for as
    // long as the fetch takes. On a 200-unit order that was ~20 seconds (CLE-2538).
    dispatch(setAutoCompleteValue(autoCompleteValue));
    dispatch(
      setSelectedProcedures(selectedProcedureArrayWithDefaultDescriptions)
    );
    dispatch(setRows(buildRows([])));
    setCurrentProviderProcedures(normalizedProviderProcedures);

    // Fetch all currently-available provider procedures so the dropdown can
    // offer alternatives. We use the result ONLY to expand each row's
    // `providerDropdown` options and to populate `currentProviderProcedures`
    // for the change handler — we do NOT touch selectedProvider, amount, or
    // providerProcedureId here, so any manual price edits on the saved order
    // survive. Refetch-driven re-pricing still only happens when the user
    // changes visit date or removes/re-adds a CPT.
    //
    // One request per DISTINCT CPT code. `selectedProceduresObject` is replicated by
    // quantity (that is how quantity is stored), so mapping over it fired one identical
    // request per unit — 200 requests for a single 200-unit code (CLE-2538).
    const dateOfServiceForSearch =
      formatDateOfServiceForProviderProcedureSearch(dateOfService);
    const distinctCptCodes = [
      ...new Set(selectedProceduresObject.map((procedure) => procedure.code)),
    ];
    const fetchedProviderProcedures: any[] = await Promise.all(
      distinctCptCodes.map(async (cptCode) => {
        const result = (await searchMutation({
          accountId,
          cptCode,
          payerName:
            orderType === orderTypeConstants.GFE
              ? serviceDetails.payerName
              : CLEAR_HEALTH_PAYER_NAME,
          ...(dateOfServiceForSearch
            ? { dateOfService: dateOfServiceForSearch }
            : {}),
        })) as any;
        return result?.data?.data?.data ?? [];
      })
    ).then((results) => results.flat());

    if (!fetchedProviderProcedures.length) return;

    // Re-paint with the expanded provider dropdowns. Quantities and saved
    // provider/amount selections are unchanged — only the dropdown options grow.
    dispatch(setRows(buildRows(fetchedProviderProcedures)));
    setCurrentProviderProcedures(fetchedProviderProcedures);
  };

  useEffect(() => {
    if (orderType !== orderTypeConstants.PATIENT_RESPONSIBILITY) {
      initializeDefault();
    }
  }, []);

  // CLE-2635 — fall back to the eligibility archive when the order carries no
  // quote, so the benefits form is not seeded with zeros.
  const benefits = resolveOrderBenefits(order);
  const secondaryBenefits = resolveOrderSecondaryBenefits(order);

  const methods = useForm({
    defaultValues: {
      email: patient.email,
      amount: centsToDollars(
        (serviceDetails as IGFEServiceDetails)
          .patientResponsibilityEstimateAmountInCents || 0
      ),
      lastName: patient.lastName,
      firstName: patient.firstName,
      dateOfBirth: parseDate(patient.dateOfBirth),
      totalAmount: centsToDollars(amountInCents || 0),
      phoneNumber: patient.phoneNumber
        ? formatPhoneNumberToMask(patient.phoneNumber)
        : null,
      accountNumber,
      accountLocationId:
        order?.accountLocation?.id ?? order?.accountLocationId ?? null,
      dateOfService: parseDate(dateOfService),
      timeOfService: parseTime(dateOfService),
      notScheduledYet: parseDate(dateOfService) === "01/01/2999",
      serviceDetails: serviceDetails.procedures,
      preferredContactMethod: patient.preferredContactMethod,
      estimatedVisitCostForPatient: estimatedVisitCost,
      orderType,
      accountId,
      payerName: payerName?.name || order?.payer?.name || null,
      payerName2: payerName2?.name || order?.secondaryPayer?.name || null,
      payer: order?.payer
        ? { name: order.payer.name, payerId: order.payer.id }
        : null,
      payer2: order?.secondaryPayer
        ? {
            name: order.secondaryPayer.name,
            payerId: order.secondaryPayer.id,
          }
        : null,
      deductible: centsToDollars(benefits.remainingDeductibleInCents),
      outOfPocketMax: centsToDollars(benefits.outOfPocketMaxInCents),
      flatCopay: centsToDollars(benefits.flatCopayInCents),
      professionalFlatCopay: centsToDollars(
        benefits.professionalFlatCopayInCents
      ),
      showProfessionalCopay: benefits.professionalFlatCopayInCents > 0,
      coinsurancePercent: benefits.coinsurancePercent,
      deductible2: centsToDollars(secondaryBenefits.remainingDeductibleInCents),
      outOfPocketMax2: centsToDollars(secondaryBenefits.outOfPocketMaxInCents),
      flatCopay2: centsToDollars(secondaryBenefits.flatCopayInCents),
      professionalFlatCopay2: centsToDollars(
        secondaryBenefits.professionalFlatCopayInCents
      ),
      showProfessionalCopay2:
        secondaryBenefits.professionalFlatCopayInCents > 0,
      coinsurancePercent2: secondaryBenefits.coinsurancePercent,
      gfeQuoteId,
      // CLE-2661 — the order's own recorded practice type wins, so a repeat inquiry asks the
      // same question the original did — but only while it is still a code we offer. A code we
      // have since removed (notably 13, which is not a practice type at all) resolves to null
      // so the account default is used instead; carrying it forward would put the wrong code
      // on a NEW estimate, invisibly, since the dropdown renders blank for an unknown value.
      // Empty here on purpose either way: PrimaryInsuranceSection/SecondaryInsuranceSection
      // fill it once the account has loaded, which it has not reliably done at this point.
      practiceType:
        knownPracticeTypeCodeOrNull(
          order?.benefitDetails?.request?.practiceTypeCode
        ) ||
        knownPracticeTypeCodeOrNull(order?.serviceDetails?.practiceTypeCode) ||
        null,
      practiceType2:
        knownPracticeTypeCodeOrNull(
          order?.secondaryBenefitDetails?.request?.practiceTypeCode
        ) ||
        knownPracticeTypeCodeOrNull(
          order?.serviceDetails?.secondaryPracticeTypeCode
        ) ||
        null,
      outOfPocketReason: serviceDetails?.outOfPocketReason
        ? { id: "", name: serviceDetails?.outOfPocketReason }
        : null,
      orderId: id,
      subscriberFirstName: patient.firstName,
      subscriberLastName: patient.lastName,
      subscriberDOB: parseDate(patient.dateOfBirth),
      memberId: patient?.patientBenefits?.memberID || null,
      memberId2: patient?.secondaryPatientBenefits?.memberID || null,
      isSubscriberPatient:
        patient?.patientBenefits?.isSubscriberPatient || null,
      paymentPlanDuration: paymentPlanActive?.paymentPlanDuration || null,
      paymentPlanDurationFull:
        (paymentPlanActive?.paymentPlanDuration || 0) + 1 || null,
      paymentPlanMinDownPaymentType:
        paymentPlanActive?.paymentPlanMinDownPaymentFlat !== null &&
        paymentPlanActive?.paymentPlanMinDownPaymentFlat !== undefined
          ? "Flat amount"
          : "Percentage",
      paymentPlanMinDownPaymentFlat:
        paymentPlanActive?.paymentPlanMinDownPaymentFlat !== null &&
        paymentPlanActive?.paymentPlanMinDownPaymentFlat !== undefined
          ? paymentPlanMinDownPaymentFlatValue
          : null,
      paymentPlanMinDownPaymentPercent:
        paymentPlanActive?.paymentPlanMinDownPaymentPercent !== null
          ? paymentPlanActive?.paymentPlanMinDownPaymentPercent
          : null,
      paymentPlanEnabled: !!paymentPlanActive,
      redoOrderFirstRender: true,
      outreachEnabled: !outreachStopped,
      providerDistributionLogic:
        paymentPlanActive?.providerDistributionLogic ||
        account?.providerDistributionLogic ||
        distributionLogicValueList.BY_PRIORITY,
      facilityCopay: !!gfeQuote?.costApplicationRules?.facilityRules?.useCopay,
      facilityDeductible:
        !!gfeQuote?.costApplicationRules?.facilityRules?.useDeductible,
      facilityCoinsurance:
        !!gfeQuote?.costApplicationRules?.facilityRules?.useCoinsurance,
      professionalCopay:
        !!gfeQuote?.costApplicationRules?.nonFacilityRules?.useCopay,
      professionalDeductible:
        !!gfeQuote?.costApplicationRules?.nonFacilityRules?.useDeductible,
      professionalCoinsurance:
        !!gfeQuote?.costApplicationRules?.nonFacilityRules?.useCoinsurance,
      secondaryInsuranceActivated: !!order?.secondaryPayerId,
      facilityCopay2:
        !!gfeQuote?.secondaryPatientBenefits?.costApplicationRules
          ?.facilityRules?.useCopay,
      facilityDeductible2:
        !!gfeQuote?.secondaryPatientBenefits?.costApplicationRules
          ?.facilityRules?.useDeductible,
      facilityCoinsurance2:
        !!gfeQuote?.secondaryPatientBenefits?.costApplicationRules
          ?.facilityRules?.useCoinsurance,
      professionalCopay2:
        !!gfeQuote?.secondaryPatientBenefits?.costApplicationRules
          ?.nonFacilityRules?.useCopay,
      professionalDeductible2:
        !!gfeQuote?.secondaryPatientBenefits?.costApplicationRules
          ?.nonFacilityRules?.useDeductible,
      professionalCoinsurance2:
        !!gfeQuote?.secondaryPatientBenefits?.costApplicationRules
          ?.nonFacilityRules?.useCoinsurance,
      benefitsUpdatedAt: gfeQuote?.benefitsUpdatedAt || null,
      secondaryBenefitsUpdatedAt:
        gfeQuote?.secondaryPatientBenefits?.benefitsUpdatedAt || null,
      // CLE-2996 — the "No deductible for this plan" choice, as the saved estimate recorded it.
      noDeductible: readNoDeductibleChoice(order?.benefitDetails),
      benefitDetails: order?.benefitDetails || null,
      benefitDetails2: order?.secondaryBenefitDetails || null,
      ...(orderType === orderTypeConstants.PATIENT_RESPONSIBILITY && {
        totalInsuranceAllowable:
          (serviceDetails as IPatientResponsibilityServiceDetails)
            ?.totalInsuranceAllowableInCents === null
            ? null
            : centsToDollars(
                (serviceDetails as IPatientResponsibilityServiceDetails)
                  ?.totalInsuranceAllowableInCents || 0
              ),
      }),
      scheduledFirstCommunicationDate: order?.scheduledFirstCommunicationDate
        ? dayjs.utc(order.scheduledFirstCommunicationDate).toDate()
        : null,
      scheduledFirstCommunicationSent:
        order?.scheduledFirstCommunicationSent || false,
      scheduledCommunicationCustomText:
        order?.scheduledCommunicationCustomText || null,
      sendDefaultText,
      initialClearPatientFeeValueInPercentage:
        order?.clearPatientFeeValueInPercentage
          ? Number(order?.clearPatientFeeValueInPercentage)
          : null,
      initialClearFeeValueInPercentage: order?.clearFeeValueInPercentage
        ? Number(order?.clearFeeValueInPercentage)
        : null,
      isDownPaymentOnly: paymentPlanActive?.isDownPaymentOnly || false,
      downpaymentOnlyType:
        paymentPlanActive?.paymentPlanMinDownPaymentFlat !== null &&
        paymentPlanActive?.paymentPlanMinDownPaymentFlat !== undefined
          ? "Flat amount"
          : "Percentage",
      downpaymentOnlyPercent:
        paymentPlanActive?.paymentPlanMinDownPaymentPercent !== null
          ? paymentPlanActive?.paymentPlanMinDownPaymentPercent
          : null,
      downpaymentOnlyFlatAmount: paymentPlanMinDownPaymentFlatValue,
      isSetAmount: paymentPlanActive?.calculationBasis === "InstallmentAmount",
      installmentFrequency:
        paymentPlanActive?.installmentFrequency || "Monthly",
      installmentAmount: paymentPlanActive?.installmentAmountInCents
        ? centsToDollars(paymentPlanActive.installmentAmountInCents)
        : null,
      excludedNcciRulesetIds: gfeQuote?.excludedNcciRulesetIds || [],
      excludedNcciInfo:
        (gfeQuote?.pricingBreakdown as any)?.excludedNcciInfo || [],
    },
  });

  return (
    <FormProvider {...methods}>
      {account && (
        <OrderFormHeader
          title="Redo Estimate"
          accountName={account.name}
          onBack={handleSwitchRedoOrder}
          onCancel={handleSwitchRedoOrder}
        />
      )}
      <div className="flex flex-column xl:flex-row gap-3 xl:align-items-start">
        <div className="flex-1" style={{ minWidth: 0 }}>
          <PatientStep
            orderTypesVisible={account?.orderTypesVisible}
            isFetching={false}
            redo
          />
          <VisitForm
            account={account}
            skipDuplicateCheck
            isRedo
            order={order}
          />
          <PatientBenefitsSection
            costApplicationRules={gfeQuote?.costApplicationRules}
            account={account}
            redo
          />
          <ServiceDetails
            redo
            selectedProceduresList={selectedProceduresList}
            totalAmount={order.amountInCents}
            currentProviderProcedures={currentProviderProcedures}
          />
          {!!account && (
            <PaymentPlan
              accountData={{ data: account }}
              paymentPlan={paymentPlanActive}
              isRedo
            />
          )}
          {account && <Communication account={account} order={order} redo />}
        </div>

        {/* CLE-2890 — seeded equal to the saved amount, so the line appears once a recalculation changes the figure. */}
        <EstimateResults previousOrder={order} />
      </div>

      <StickyFooter>
        <Submit redo />
      </StickyFooter>
    </FormProvider>
  );
}
